import type { PriceDisplay, PriceTag, Promotion } from '@/app/lib/types'
import { pickSpecialPromotion } from './pickSpecialPromotion'

export interface ResolveEventPriceInput {
  minPrice: number | null
  originalPrice: number | null
  /** Ya filtradas por vigencia — ver `filterVisiblePromotions`. */
  visiblePromotions: Promotion[]
  hasAppPromotions: boolean
  /** Precio final ya parseado (FR-018, `parsePriceField`). Con valor, sustituye
   * a todo el cálculo de mejor precio. `null`/ausente ⇒ lógica de siempre. */
  salePrice?: number | null
  /** Porcentaje del backend ya parseado (FR-019). `null`/ausente ⇒ se calcula. */
  discountPercentage?: number | null
}

/** Redondeo al entero más cercano (research.md §4). `null` si no hay base de
 * comparación válida (evita división por cero/negativos). */
function discountPercentage(originalPrice: number | null, amount: number): number | null {
  if (originalPrice == null || originalPrice <= 0 || amount >= originalPrice) return null
  return Math.round(((originalPrice - amount) / originalPrice) * 100)
}

function promotionTag(promotion: Promotion): PriceTag {
  return promotion.promotionLabel ? { kind: 'label', text: promotion.promotionLabel } : { kind: 'icon' }
}

/**
 * Implementa FR-002 a FR-011 (tabla de decisión en
 * contracts/resolveEventPrice.md). Función pura: sin React, sin red, sin
 * cookies — el override "App" (FR-014/FR-015) lo decide el átomo cliente
 * `PriceTag` a partir de `hasAppPromotions`, no esta función.
 */
export function resolveEventPrice({
  minPrice,
  originalPrice,
  visiblePromotions,
  hasAppPromotions,
  salePrice = null,
  discountPercentage: backendPercentage = null,
}: ResolveEventPriceInput): PriceDisplay {
  const special = pickSpecialPromotion(visiblePromotions)

  // FR-018/FR-019: el precio final del backend gana a min_price, a las
  // promociones con price y a la promoción especial (con o sin price).
  if (salePrice != null) {
    const [firstPromotion] = visiblePromotions
    if (originalPrice != null && salePrice < originalPrice) {
      const label = special?.promotionLabel ?? visiblePromotions.find((p) => p.promotionLabel)?.promotionLabel
      const percentage = backendPercentage ?? discountPercentage(originalPrice, salePrice)
      const tag: PriceTag | null = label
        ? { kind: 'label', text: label }
        : percentage != null
          ? { kind: 'discount', percentage }
          : null
      return { amount: salePrice, isFree: salePrice === 0, originalAmount: originalPrice, tag, hasAppPromotions }
    }
    return {
      amount: salePrice,
      isFree: salePrice === 0,
      originalAmount: null,
      tag: firstPromotion ? promotionTag(firstPromotion) : null,
      hasAppPromotions,
    }
  }

  // Casos C.1/C.2 (FR-008b/FR-009): la promoción especial gana siempre,
  // con independencia de min_price.
  if (special) {
    if (special.price != null) {
      const amount = special.price
      const percentage = discountPercentage(originalPrice, amount)
      // Clarifications (sesión 2026-07-22): con price informado, el
      // promotionlabel de esa misma promoción especial tiene prioridad sobre
      // el "-XX%" calculado — mismo criterio "texto CMS sobre valor
      // calculado" que ya aplican FR-009/FR-010 para el icono.
      const tag: PriceTag | null = special.promotionLabel
        ? { kind: 'label', text: special.promotionLabel }
        : percentage != null
          ? { kind: 'discount', percentage }
          : null
      return {
        amount,
        isFree: amount === 0,
        originalAmount: originalPrice,
        tag,
        hasAppPromotions,
      }
    }

    const amount = originalPrice ?? minPrice ?? 0
    return {
      amount,
      isFree: amount === 0,
      originalAmount: null,
      tag: promotionTag(special),
      hasAppPromotions,
    }
  }

  // Casos A/B (FR-005/FR-006/FR-007): el mejor precio disponible (min_price y/o
  // el price de cualquier promoción no especial vigente) compite siempre
  // contra original_price — con independencia de si min_price ya era, por sí
  // solo, menor que original_price (Clarifications, sesión 2026-07-22: una
  // promoción con price=0 hace el evento gratis aunque min_price==original_price).
  const candidates: { price: number; promotion: Promotion | null }[] = []
  if (minPrice != null) candidates.push({ price: minPrice, promotion: null })
  for (const promotion of visiblePromotions) {
    if (promotion.price != null) candidates.push({ price: promotion.price, promotion })
  }

  if (originalPrice != null && candidates.length > 0) {
    const best = candidates.reduce((lowest, candidate) =>
      candidate.price < lowest.price ? candidate : lowest,
    )

    if (best.price < originalPrice) {
      const percentage = discountPercentage(originalPrice, best.price)
      // El promotionlabel solo manda cuando es la promoción ganadora la que
      // fija el precio mostrado (misma prioridad "texto CMS sobre valor
      // calculado" que FR-008b/FR-009/FR-010); si gana min_price a pelo, el
      // tag es siempre el porcentaje.
      const tag: PriceTag | null = best.promotion?.promotionLabel
        ? { kind: 'label', text: best.promotion.promotionLabel }
        : percentage != null
          ? { kind: 'discount', percentage }
          : null

      return {
        amount: best.price,
        isFree: best.price === 0,
        originalAmount: originalPrice,
        tag,
        hasAppPromotions,
      }
    }
  }

  // Caso D (FR-010) / sin rebaja (ni de min_price ni de ninguna promoción):
  // precio original (o el único disponible) + tag informativo si hay alguna
  // promoción no especial vigente.
  const amount = originalPrice ?? minPrice ?? 0
  const [firstPromotion] = visiblePromotions

  return {
    amount,
    isFree: amount === 0,
    originalAmount: null,
    tag: firstPromotion ? promotionTag(firstPromotion) : null,
    hasAppPromotions,
  }
}
